import React, { useState, useEffect, useRef } from 'react';

const GoalVisualization = ({ goal, currentBalance }) => {
  const [animatedProgress, setAnimatedProgress] = useState(0);
  const progress = goal ? Math.min((currentBalance / goal.target_amount) * 100, 100) : 0;
  const remaining = goal ? Math.max(goal.target_amount - currentBalance, 0) : 0;
  const isComplete = goal && currentBalance >= goal.target_amount;
  const animRef = useRef(null);

  useEffect(() => {
    // Animate progress bar
    let start = 0;
    const duration = 1200;
    const startTime = performance.now();

    const animate = (now) => {
      const elapsed = now - startTime;
      const t = Math.min(elapsed / duration, 1);
      // Ease-out cubic
      const eased = 1 - Math.pow(1 - t, 3);
      setAnimatedProgress(eased * progress);

      if (t < 1) {
        animRef.current = requestAnimationFrame(animate);
      }
    };

    animRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animRef.current);
  }, [progress]);

  if (!goal) {
    return (
      <div className="gv-panel">
        <div className="gv-empty">
          <span className="gv-empty-emoji">🎯</span>
          <h4>No active goal</h4>
          <p>Set a savings goal to track your progress!</p>
        </div>
        <style>{gvStyles}</style>
      </div>
    );
  }

  const getProgressColor = () => {
    if (progress >= 100) return '#10B981';
    if (progress >= 75) return '#3B82F6';
    if (progress >= 50) return '#F59E0B';
    if (progress >= 25) return '#F97316';
    return '#EF4444';
  };

  const getEmoji = () => {
    if (isComplete) return '🎉';
    if (progress >= 75) return '🚀';
    if (progress >= 50) return '⭐';
    if (progress >= 25) return '💪';
    return '🌱';
  };

  return (
    <div className="gv-panel">
      <div className="gv-card" style={{ background: isComplete ? 'linear-gradient(135deg, #10B981, #059669)' : 'linear-gradient(135deg, #1E293B, #334155)' }}>
        {/* Header */}
        <div className="gv-header">
          <span className="gv-goal-emoji">{goal.image_url || '🎯'}</span>
          <div className="gv-title-area">
            <h3 className="gv-title">{goal.title}</h3>
            {goal.description && <p className="gv-desc">{goal.description}</p>}
          </div>
          <span className="gv-status-emoji">{getEmoji()}</span>
        </div>

        {/* Stats */}
        <div className="gv-stats">
          <div className="gv-stat">
            <span className="gv-stat-value">{currentBalance}</span>
            <span className="gv-stat-label">SAVED</span>
          </div>
          <div className="gv-divider" />
          <div className="gv-stat">
            <span className="gv-stat-value">{goal.target_amount}</span>
            <span className="gv-stat-label">GOAL</span>
          </div>
          <div className="gv-divider" />
          <div className="gv-stat">
            <span className="gv-stat-value">{remaining}</span>
            <span className="gv-stat-label">TO GO</span>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="gv-track">
          <div className="gv-fill" style={{ width: `${animatedProgress}%`, background: `linear-gradient(90deg, ${getProgressColor()}, ${getProgressColor()}cc)` }} />
          {[25, 50, 75].map(m => (
            <div key={m} className={`gv-milestone ${progress >= m ? 'active' : ''}`} style={{ left: `${m}%` }} />
          ))}
        </div>

        {/* Footer */}
        <div className="gv-footer">
          <span className="gv-percent">{Math.round(progress)}% complete</span>
          {!isComplete && remaining > 0 && (
            <span className="gv-projection">~{Math.ceil(remaining / 5)} more chores!</span>
          )}
        </div>

        {/* Celebration */}
        {isComplete && (
          <div className="gv-celebration">🎉 Goal Reached! Time to celebrate! 🎉</div>
        )}
      </div>

      {/* Countdown */}
      {!isComplete && remaining > 0 && (
        <GoalCountdown goal={goal} currentBalance={currentBalance} remaining={remaining} progress={progress} />
      )}

      <style>{gvStyles}</style>
    </div>
  );
};

const GoalCountdown = ({ goal, currentBalance, remaining, progress }) => {
  const avgPerDay = 5;
  const daysRemaining = Math.ceil(remaining / avgPerDay);

  return (
    <div className="gc-card">
      <div className="gc-header">
        <span className="gc-emoji">⏳</span>
        <div>
          <h4 className="gc-title">{remaining} DB to go</h4>
          <p className="gc-sub">~{daysRemaining} days at current pace</p>
        </div>
      </div>
      <div className="gc-runway">
        <div className="gc-runway-fill" style={{ width: `${progress}%` }} />
        <span className="gc-plane">✈️</span>
      </div>
      <div className="gc-markers">
        <span>Today</span>
        <span>~{Math.ceil(daysRemaining / 2)}d</span>
        <span>{daysRemaining}d</span>
      </div>
    </div>
  );
};

const gvStyles = `
  .gv-panel { margin-bottom: 24px; }
  .gv-card { border-radius: 20px; padding: 24px; color: #fff; }
  .gv-header { display: flex; align-items: center; margin-bottom: 20px; }
  .gv-goal-emoji { font-size: 32px; margin-right: 12px; }
  .gv-title-area { flex: 1; }
  .gv-title { margin: 0; font-size: 18px; font-weight: 700; }
  .gv-desc { margin: 2px 0 0; font-size: 12px; opacity: 0.7; }
  .gv-status-emoji { font-size: 28px; }
  .gv-stats { display: flex; justify-content: space-around; margin-bottom: 20px; }
  .gv-stat { text-align: center; }
  .gv-stat-value { display: block; font-size: 24px; font-weight: 800; }
  .gv-stat-label { font-size: 10px; opacity: 0.6; letter-spacing: 1px; margin-top: 2px; display: block; }
  .gv-divider { width: 1px; background: rgba(255,255,255,0.2); margin: 4px 0; }
  .gv-track { height: 12px; background: rgba(255,255,255,0.15); border-radius: 6px; position: relative; overflow: visible; }
  .gv-fill { height: 12px; border-radius: 6px; transition: width 0.3s ease; }
  .gv-milestone { position: absolute; top: -4px; width: 10px; height: 10px; border-radius: 50%; background: rgba(255,255,255,0.2); border: 2px solid rgba(255,255,255,0.1); transform: translateX(-5px); }
  .gv-milestone.active { background: #fff; border-color: #3B82F6; }
  .gv-footer { display: flex; justify-content: space-between; margin-top: 8px; font-size: 13px; opacity: 0.6; }
  .gv-percent { font-weight: 600; }
  .gv-celebration { text-align: center; background: rgba(255,255,255,0.15); border-radius: 12px; padding: 12px; margin-top: 12px; font-weight: 700; animation: gvPulse 2s infinite; }
  @keyframes gvPulse { 0%, 100% { opacity: 0.8; } 50% { opacity: 1; } }
  .gv-empty { text-align: center; padding: 32px; background: #F9FAFB; border-radius: 16px; border: 2px dashed #E5E7EB; }
  .gv-empty-emoji { font-size: 40px; display: block; margin-bottom: 8px; }
  .gv-empty h4 { margin: 0; color: #6B7280; }
  .gv-empty p { margin: 4px 0 0; color: #9CA3AF; font-size: 13px; }
  .gc-card { background: #fff; border-radius: 16px; padding: 16px; margin-top: 12px; box-shadow: 0 1px 3px rgba(0,0,0,0.1); }
  .gc-header { display: flex; align-items: center; gap: 12px; margin-bottom: 12px; }
  .gc-emoji { font-size: 28px; }
  .gc-title { margin: 0; font-size: 18px; font-weight: 700; color: #1F2937; }
  .gc-sub { margin: 2px 0 0; font-size: 12px; color: #9CA3AF; }
  .gc-runway { height: 8px; background: #E5E7EB; border-radius: 4px; position: relative; overflow: visible; }
  .gc-runway-fill { height: 8px; background: #3B82F6; border-radius: 4px; transition: width 0.6s ease; }
  .gc-plane { position: absolute; top: -12px; right: 0; font-size: 20px; }
  .gc-markers { display: flex; justify-content: space-between; margin-top: 6px; font-size: 11px; color: #9CA3AF; }
`;

export default GoalVisualization;
